import React, { useState, useRef, useEffect } from 'react';
import './FillInTheGapsExercise.css';

const FillInTheGapsExercise = ({ sentences: initialSentences, title, level, onClose, updateAttributes, node, editor, getPos, isStudent = false, myState = {}, otherState = {}, updateState = () => {} }) => {
  const [sentences, setSentences] = useState(initialSentences);
  const [userAnswers, setUserAnswers] = useState({});
  const syncedRef = React.useRef(false);
  useEffect(() => {
    if (syncedRef.current) return;
    const hasAnswers = myState.answers && Object.keys(myState.answers).length > 0;
    const hasResult = myState.showResult === true;
    if (hasAnswers || hasResult) {
      syncedRef.current = true;
      if (myState.answers) setUserAnswers(normalizeAnswers(myState.answers));
      if (hasResult) setShowResult(true);
    }
  }, [myState.answers, myState.showResult]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showResult, setShowResult] = useState(false);

  // When other side submits, show results here too
  useEffect(() => {
    if (otherState.showResult === true && !showResult) {
      setShowResult(true);
    }
  }, [otherState.showResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // When other side resets, reset here too
  useEffect(() => {
    if (!otherState.resetAt) return;
    if (prevResetAtRef.current === otherState.resetAt) return;
    prevResetAtRef.current = otherState.resetAt;
    setShowResult(false);
    setUserAnswers({});
    setSelectedWord(null);
    syncedRef.current = false;
  }, [otherState.resetAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const [editing, setEditing] = useState({ idx: null, field: null });
  const [editValue, setEditValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableLevel, setEditableLevel] = useState(level);
  const [selectedWord, setSelectedWord] = useState(null);
  const addBtnRef = useRef(null);
  const prevResetAtRef = useRef(null);
  const containerRef = useRef(null);
  const wordBoxRef = useRef(null);
  const editTextareaRefs = useRef({});


  // Float word box down with scroll, stop at exercise bottom (position:absolute in position:relative container)
  useEffect(() => {
    const container = containerRef.current;
    const wordBox = wordBoxRef.current;
    if (!container || !wordBox) return;

    const getScrollParent = (el) => {
      if (!el || el === document.body) return window;
      const { overflowY } = getComputedStyle(el);
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el;
      return getScrollParent(el.parentElement);
    };

    const THRESHOLD = 80; // 72px toolbar + 8px gap

    const update = () => {
      const containerRect = container.getBoundingClientRect();
      const wbHeight = wordBox.offsetHeight;
      const naturalY = wordBox.offsetTop;
      const wordBoxViewportY = containerRect.top + naturalY;
      const scrolledPast = Math.max(0, THRESHOLD - wordBoxViewportY);

      if (scrolledPast > 0) {
        const maxTranslate = container.offsetHeight - naturalY - wbHeight - 8;
        const translateY = Math.min(scrolledPast, Math.max(0, maxTranslate));
        wordBox.style.transform = `translateY(${translateY}px)`;
        wordBox.style.position = 'relative';
        wordBox.style.zIndex = '50';
        wordBox.style.boxShadow = '0 6px 24px rgba(102,126,234,0.18)';
        wordBox.style.transition = 'box-shadow 0.2s ease';
      } else {
        wordBox.style.transform = '';
        wordBox.style.position = '';
        wordBox.style.zIndex = '';
        wordBox.style.boxShadow = '';
        wordBox.style.transition = '';
      }
    };

    const scrollParent = getScrollParent(container);
    update();
    scrollParent.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    return () => {
      scrollParent.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract all gap words from sentences (supports multiple **word** per sentence)
  const getAllGapWords = (sArr) => {
    const words = [];
    (sArr || []).forEach(s => {
      const matches = [...(s.sentence || '').matchAll(/\*\*(.+?)\*\*/g)];
      if (matches.length > 0) matches.forEach(m => words.push(m[1]));
      else if (s.gap) words.push(s.gap);
    });
    return words;
  };

  // Get the correct answer for a gap key like "sentenceId_gapIndex"
  const getCorrectAnswer = (gapKey) => {
    const parts = String(gapKey).split('_');
    const sentenceId = parseInt(parts[0]);
    const gapIndex = parseInt(parts[1] ?? 0);
    const sentence = sentences.find(s => s.id === sentenceId);
    if (!sentence) return '';
    const matches = [...(sentence.sentence || '').matchAll(/\*\*(.+?)\*\*/g)];
    if (matches.length > 0) return matches[gapIndex]?.[1] || sentence.gap || '';
    return sentence.gap || '';
  };

  // Normalize old-format answers {sentenceId: val} → {"sentenceId_0": val}
  const normalizeAnswers = (ans) => {
    if (!ans) return {};
    const normalized = {};
    for (const [key, val] of Object.entries(ans)) {
      normalized[String(key).includes('_') ? key : `${key}_0`] = val;
    }
    return normalized;
  };

  // Получаем все уникальные слова для word box (стабильный порядок между рендерами)
  const getShuffled = arr => arr.map(v => [Math.random(), v]).sort((a, b) => a[0] - b[0]).map(x => x[1]);
  const [allWords, setAllWords] = useState(() => getShuffled([...new Set(getAllGapWords(initialSentences))]));
  const wordsShuffleInitRef = useRef(false);
  const lastBroadcastWordsRef = useRef(null);

  // Broadcast our own shuffle order once so both sides see the SAME word
  // bank order — this was previously purely local/random per side, which
  // is exactly the kind of Follow Mode divergence students kept reporting
  // ("watching mode shows something different from what the teacher sees").
  useEffect(() => {
    if (allWords.length === 0 || wordsShuffleInitRef.current) return;
    wordsShuffleInitRef.current = true;
    lastBroadcastWordsRef.current = allWords;
    updateState({ allWords });
  }, [allWords]); // eslint-disable-line react-hooks/exhaustive-deps

  // Adopt the other side's order once it arrives.
  useEffect(() => {
    if (!Array.isArray(otherState.allWords) || otherState.allWords.length === 0) return;
    if (lastBroadcastWordsRef.current === otherState.allWords) return;
    wordsShuffleInitRef.current = true;
    setAllWords(prev => {
      const unique = [...new Set(getAllGapWords(sentences))];
      const fromRemote = otherState.allWords.filter(w => unique.includes(w));
      const missing = unique.filter(w => !fromRemote.includes(w));
      return [...fromRemote, ...getShuffled(missing)];
    });
  }, [otherState.allWords]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setAllWords(prev => {
      const unique = [...new Set(getAllGapWords(sentences))];
      const existingInOrder = prev.filter(w => unique.includes(w));
      const newOnes = unique.filter(w => !prev.includes(w));
      const next = [...existingInOrder, ...getShuffled(newOnes)];
      // Re-broadcast if the word set actually changed (e.g. a sentence was
      // edited/added), so a newly-added word's position reaches the peer
      // too instead of each side shuffling it in independently.
      if (newOnes.length > 0) {
        lastBroadcastWordsRef.current = next;
        updateState({ allWords: next });
      }
      return next;
    });
  }, [sentences]); // eslint-disable-line react-hooks/exhaustive-deps

  // Merge local + remote answers: local takes priority per gap key (mirrors displayValue logic)
  const getMergedAnswers = () => {
    const localNorm = normalizeAnswers(userAnswers);
    const remoteNorm = normalizeAnswers(otherState.answers || {});
    const allKeys = new Set([...Object.keys(localNorm), ...Object.keys(remoteNorm)]);
    const merged = {};
    allKeys.forEach(key => {
      const local = localNorm[key];
      merged[key] = (local !== undefined && local !== '') ? local : (remoteNorm[key] || '');
    });
    return merged;
  };

  // Count how many gaps currently display a given word (local + remote combined)
  const getWordUsage = (word) => {
    return Object.values(getMergedAnswers()).filter(answer => answer === word).length;
  };

  const handleAnswerChange = (sentenceId, answer) => {
    setUserAnswers(prev => {
      const next = { ...prev, [sentenceId]: answer };
      updateState({ answers: next });
      return next;
    });
  };

  const handleCheck = () => {
    setShowResult(true);
    updateState({ showResult: true, answers: userAnswers });
  };

  const handleReset = () => {
    setShowResult(false);
    setUserAnswers({});
    setSelectedWord(null);
    updateState({ showResult: false, answers: {}, resetAt: Date.now() });
  };

  // eslint-disable-next-line no-unused-vars
  const startEdit = (idx, field, value) => {
    setEditing({ idx, field });
    setEditValue(value);
  };

  const commitEdit = () => {
    if (editing.idx === null) return;
    setSentences(prev => prev.map((s, i) =>
      i === editing.idx ? { ...s, [editing.field]: editValue } : s
    ));
    setEditing({ idx: null, field: null });
    setEditValue('');
  };

  // eslint-disable-next-line no-unused-vars
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      setEditing({ idx: null, field: null });
      setEditValue('');
    }
  };

  const handleAddSentence = () => {
    const newSentence = { 
      id: Date.now(), 
      sentence: 'This is a sample sentence with **example** for practice.', 
      gap: 'example' 
    };
    setSentences(prev => [...prev, newSentence]);
  };

  const handleDeleteSentence = (index) => {
    if (sentences.length <= 1) return; // Не удаляем последнее предложение
    
    setSentences(prev => prev.filter((_, i) => i !== index));
  };

  const isAnswerCorrect = (gapKey, userAnswer) => {
    return userAnswer.toLowerCase().trim() === getCorrectAnswer(gapKey).toLowerCase().trim();
  };

  const getTotalGapsCount = () => sentences.reduce((sum, s) => {
    const n = (s.sentence.match(/\*\*(.+?)\*\*/g) || []).length;
    return sum + (n > 0 ? n : 1);
  }, 0);

  // Score against the same merged local+remote answers shown per-gap in
  // renderSentence — using only otherState.answers here caused the stats
  // panel to disagree with the individual gap highlighting whenever the
  // local side's own answers hadn't (yet) round-tripped through sync.
  const getAnswersToCheck = () => getMergedAnswers();

  const getCorrectAnswersCount = () => {
    const answers = getAnswersToCheck();
    return Object.keys(answers).filter(k => isAnswerCorrect(k, answers[k])).length;
  };

  const getIncorrectAnswersCount = () => {
    const total = getTotalGapsCount();
    const correct = getCorrectAnswersCount();
    return Math.max(0, total - correct);
  };

  // Results panel data — same ring/tier pattern as the other exercises,
  // kept in this exercise's own violet accent.
  const getResultsData = () => {
    const total = getTotalGapsCount() || 1;
    const correct = getCorrectAnswersCount();
    const ratio = correct / total;
    const percent = Math.round(ratio * 100);
    const circumference = 2 * Math.PI * 42;
    const dashOffset = circumference * (1 - percent / 100);
    let tier = 'poor';
    let headline;
    let subline;
    if (ratio >= 0.9) { tier = 'excellent'; headline = 'Outstanding!'; subline = isStudent ? 'You nailed it.' : 'Student nailed it.'; }
    else if (ratio >= 0.7) { tier = 'great'; headline = 'Great job!'; subline = 'Solid performance.'; }
    else if (ratio >= 0.5) { tier = 'good'; headline = 'Good work.'; subline = isStudent ? 'Keep going — you’re close.' : 'Getting there.'; }
    else { tier = 'poor'; headline = 'Keep practicing'; subline = isStudent ? 'You’ll get it — try again.' : 'Review and try again.'; }
    return { percent, circumference, dashOffset, tier, headline, subline, correct, incorrect: getIncorrectAnswersCount(), total };
  };

  // Word selection and insertion handlers
  const handleWordSelect = (word) => {
    setSelectedWord(word);
  };

  const handleFieldClick = (gapKey) => {
    if (selectedWord) {
      handleAnswerChange(gapKey, selectedWord);
      setSelectedWord(null);
    }
  };

  const renderSentence = (sentence) => {
    const parts = sentence.sentence.split(/\*\*(.*?)\*\*/);
    const localNorm = normalizeAnswers(userAnswers);
    const remoteNorm = normalizeAnswers(otherState.answers || {});

    return parts.map((part, index) => {
      if (index % 2 === 1) {
        const gapIndex = Math.floor(index / 2);
        const gapKey = `${sentence.id}_${gapIndex}`;
        const displayValue = (localNorm[gapKey] !== undefined && localNorm[gapKey] !== '')
          ? localNorm[gapKey]
          : (remoteNorm[gapKey] || '');
        const isCorrect = showResult && displayValue !== '';
        const isRight = isCorrect && isAnswerCorrect(gapKey, displayValue);
        const isSelected = selectedWord && !localNorm[gapKey];
        // Teacher-only affordance: highlight which gap a selected word
        // belongs to. Gated on !isStudent so the student's view never
        // reveals the answer — this is what makes the exercise a real
        // exercise for them instead of a solved worksheet.
        const isCorrectTarget = !isStudent && !showResult && selectedWord && getCorrectAnswer(gapKey) === selectedWord && !localNorm[gapKey];

        return (
          <span key={index} style={{ display: 'inline' }}>
            <input
              type="text"
              value={displayValue}
              onChange={!showResult ? (e) => handleAnswerChange(gapKey, e.target.value) : undefined}
              onClick={!showResult ? () => handleFieldClick(gapKey) : undefined}
              disabled={showResult}
              readOnly={showResult}
              className={`gap-input ${showResult ? (isRight ? 'correct' : 'incorrect') : ''} ${isSelected ? 'selected' : ''}`}
              placeholder={isCorrectTarget ? `↑ ${selectedWord}` : isSelected ? `Click to insert "${selectedWord}"` : 'Type your answer'}
              style={{
                border: showResult ? (isRight ? '2px solid #28a745' : '2px solid #dc3545') :
                       isCorrectTarget ? '2px solid #f59e0b' :
                       isSelected ? '2px solid #ffc107' :
                       displayValue ? '2px solid #c0c0c0' : '2px solid #8a2be2',
                backgroundColor: showResult ? (isRight ? '#d4edda' : '#f8d7da') :
                               isCorrectTarget ? '#fef3c7' :
                               isSelected ? '#fff3cd' :
                               displayValue ? '#f5f5f5' : '#fff',
                color: showResult ? (isRight ? '#155724' : '#721c24') : isCorrectTarget ? '#92400e' : '#000',
                fontWeight: isCorrectTarget ? 600 : 400,
                minWidth: '120px',
                padding: '4px 8px',
                borderRadius: '4px',
                margin: '0 4px',
                transition: 'all 0.2s ease',
                cursor: isSelected ? 'pointer' : 'text',
                boxShadow: isCorrectTarget ? '0 0 0 3px rgba(245,158,11,0.25)' : 'none',
              }}
            />
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className="fitg-card" ref={containerRef} style={{ position: 'relative' }}>
      <div className="fitg-header">
        <div className="fitg-header-left">
          <div className="fitg-header-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="2"/>
              <path d="M7 9h3M7 13h6"/>
              <rect x="15" y="12" width="4" height="4" rx="0.5" strokeDasharray="1.6 1.6"/>
            </svg>
          </div>
          {isEditing ? (
            <input type="text" value={editableTitle} onChange={e => setEditableTitle(e.target.value)} className="fitg-title-input" />
          ) : (
            <span className="fitg-title">{editableTitle}</span>
          )}
          <span className="fitg-level-badge">{editableLevel}</span>
        </div>
        <div className="fitg-header-actions">
          {!isEditing && !isStudent && (
            <button className="fitg-icon-btn" onClick={() => setIsEditing(true)} aria-label="Edit" data-tip="Edit">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {isEditing && (
            <>
              <button className="fitg-icon-btn is-save" onClick={() => {
                setIsEditing(false);
                if (updateAttributes) {
                  updateAttributes({
                    sentences: sentences,
                    title: editableTitle,
                    level: editableLevel,
                  });
                }
              }} aria-label="Save" data-tip="Save">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9,12 11,14 15,10" />
                </svg>
              </button>
              <button className="fitg-icon-btn is-cancel" onClick={() => { setIsEditing(false); setEditableTitle(title); setEditableLevel(level); }} aria-label="Cancel" data-tip="Cancel">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </>
          )}
          {onClose && (
            <button className="fitg-icon-btn" onClick={onClose} aria-label="Close" data-tip="Close">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="fitg-inner-body">
      <div className="exercise-instructions">
        {isEditing ? (
          <p>
            Add your own sentence and mark the gap word with **double asterisks**.
            Example: The weather is **sunny** today. Then set the exact answer in "Correct answer".
          </p>
        ) : (
          <p>Click on a word to select it, then click on a gap to insert it. You can also type answers manually.</p>
        )}
      </div>

      <div className="fitg-body">
        {!isEditing && (
          <div>
            <div ref={wordBoxRef} className="word-box">
            <div className="word-list">
              {allWords.map((word, index) => {
                const usage = getWordUsage(word);
                const totalOccurrences = sentences.reduce((count, s) => {
                  const matches = [...(s.sentence || '').matchAll(/\*\*(.+?)\*\*/g)];
                  return count + (matches.length > 0
                    ? matches.filter(m => m[1] === word).length
                    : (s.gap === word ? 1 : 0));
                }, 0);
                const isSelected = selectedWord === word;
                const isUsed = totalOccurrences > 0 && usage >= totalOccurrences;
                const remaining = totalOccurrences - usage;
                return (
                  <span
                    key={index}
                    className={`word-item ${isSelected ? 'selected' : ''} ${isUsed ? 'used' : ''}`}
                    onClick={() => handleWordSelect(word)}
                    style={{ opacity: showResult ? 0.6 : 1, cursor: showResult ? 'default' : 'pointer' }}
                  >
                    {word}
                    {totalOccurrences > 1 && remaining > 0 && (
                      <span style={{ fontSize: '0.65em', marginLeft: 5, padding: '1px 5px', borderRadius: 10, border: '1.5px solid rgba(138,43,226,0.5)', background: 'rgba(255,255,255,0.9)', color: '#7b2fbe', lineHeight: 1 }}>×{remaining}</span>
                    )}
                  </span>
                );
              })}
            </div>
            {selectedWord && (
              <div className="selected-word-hint">
                Selected: <strong>{selectedWord}</strong> — Click on a gap to insert
              </div>
            )}
          </div>
          </div>
        )}
        <div className="sentences-section">
          {sentences.map((sentence, index) => (
              <div key={sentence.id} className="sentence-container">
                <div className="sentence-content">
                  {isEditing ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ position: 'relative', marginBottom: 4 }}>
                          <textarea
                            ref={el => {
                              editTextareaRefs.current[sentence.id] = el;
                              if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; }
                            }}
                            value={sentence.sentence}
                            className="fitg-edit-textarea"
                            onChange={e => {
                              const el = e.target;
                              el.style.height = 'auto';
                              el.style.height = `${el.scrollHeight}px`;
                              const val = el.value;
                              const firstGap = val.match(/\*\*(.+?)\*\*/)?.[1] || sentence.gap;
                              setSentences(prev => prev.map((s, i) => i === index ? { ...s, sentence: val, gap: firstGap } : s));
                            }}
                          />
                          <button
                            title="Mark selected word as gap"
                            onMouseDown={e => {
                              e.preventDefault();
                              const ta = editTextareaRefs.current[sentence.id];
                              if (!ta) return;
                              const start = ta.selectionStart;
                              const end = ta.selectionEnd;
                              if (start === end) return;
                              const selected = ta.value.substring(start, end).trim();
                              if (!selected) return;
                              const newVal = ta.value.substring(0, start) + `**${selected}**` + ta.value.substring(end);
                              const firstGap = newVal.match(/\*\*(.+?)\*\*/)?.[1] || sentence.gap;
                              setSentences(prev => prev.map((s, i) => i === index ? { ...s, sentence: newVal, gap: firstGap } : s));
                              setTimeout(() => { ta.focus(); ta.setSelectionRange(start + 2, start + 2 + selected.length); }, 0);
                            }}
                            style={{ position: 'absolute', top: 6, right: 6, width: 28, height: 28, borderRadius: 6, border: '1.5px solid #cfcfff', background: '#fff', cursor: 'pointer', fontWeight: 800, fontSize: 14, color: '#8a2be2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >B</button>
                        </div>
                        {(() => {
                          const gaps = [...(sentence.sentence || '').matchAll(/\*\*(.+?)\*\*/g)].map(m => m[1]);
                          return gaps.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                              <span style={{ fontSize: 11, color: '#9ca3af', alignSelf: 'center', marginRight: 2 }}>Gaps:</span>
                              {gaps.map((g, i) => (
                                <span key={i} style={{ fontSize: 12, fontWeight: 600, color: '#8a2be2', background: '#f3e8ff', borderRadius: 5, padding: '2px 8px', border: '1px solid #d8b4fe' }}>{g}</span>
                              ))}
                            </div>
                          ) : (
                            <small style={{ color: '#9ca3af', fontSize: 11 }}>Select a word → press <strong style={{color:'#8a2be2'}}>B</strong> to mark as gap</small>
                          );
                        })()}
                      </div>
                      {sentences.length > 1 && (
                        <button
                          onClick={() => handleDeleteSentence(index)}
                          style={{
                            background: 'rgba(220, 53, 69, 0.8)',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 2,
                            borderRadius: '50%',
                            color: '#fff',
                            fontSize: 10,
                            width: 16,
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            opacity: 0.7,
                            transform: 'scale(0.9)',
                            flexShrink: 0,
                            marginTop: 2
                          }}
                          onMouseEnter={e => {
                            e.target.style.background = '#dc3545';
                            e.target.style.opacity = '1';
                            e.target.style.transform = 'scale(1)';
                          }}
                          onMouseLeave={e => {
                            e.target.style.background = 'rgba(220, 53, 69, 0.8)';
                            e.target.style.opacity = '0.7';
                            e.target.style.transform = 'scale(0.9)';
                          }}
                          title="Delete sentence"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: 16, lineHeight: 1.5 }}>
                      {renderSentence(sentence)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {isEditing && (
            <div className="add-pair-container">
              <button
                ref={addBtnRef}
                onClick={handleAddSentence}
                className="add-pair-btn"
                title="Add new sentence"
              >
                + Add new sentence
              </button>
            </div>
          )}
        </div>

      {!isEditing && (
        <div className="exercise-controls">
          {showResult ? (() => {
            const r = getResultsData();
            return (
              <div className={`fitg-results tier-${r.tier}`}>
                <div className="fitg-results-ring-wrap">
                  <svg viewBox="0 0 100 100" className="fitg-results-ring" aria-hidden>
                    <circle cx="50" cy="50" r="42" className="fitg-results-ring-track" />
                    <circle
                      cx="50"
                      cy="50"
                      r="42"
                      className="fitg-results-ring-progress"
                      style={{ strokeDasharray: r.circumference, strokeDashoffset: r.dashOffset }}
                    />
                  </svg>
                  <div className="fitg-results-ring-center">
                    <div className="fitg-results-percent">{r.percent}%</div>
                    <div className="fitg-results-percent-label">score</div>
                  </div>
                </div>
                <div className="fitg-results-headline">{r.headline}</div>
                <div className="fitg-results-subline">
                  {isStudent ? 'Your score' : "Student's score"}: {r.correct} / {r.total} — {r.subline}
                </div>
                <div className="fitg-results-stats">
                  <div className="fitg-results-stat is-correct">
                    <span className="fitg-results-stat-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                    <span className="fitg-results-stat-value">{r.correct}</span>
                    <span className="fitg-results-stat-label">correct</span>
                  </div>
                  <div className="fitg-results-stats-divider" aria-hidden />
                  <div className="fitg-results-stat is-incorrect">
                    <span className="fitg-results-stat-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </span>
                    <span className="fitg-results-stat-value">{r.incorrect}</span>
                    <span className="fitg-results-stat-label">incorrect</span>
                  </div>
                </div>
                <button className="fitg-results-cta" onClick={handleReset}>
                  <span>Try Again</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="23 4 23 10 17 10"/>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                  </svg>
                </button>
              </div>
            );
          })() : (
            <button
              className="check-btn"
              onClick={handleCheck}
              disabled={Object.keys(userAnswers).length < getTotalGapsCount()}
            >
              Check Answers
            </button>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default FillInTheGapsExercise; 